import { CartItem } from '@/store/cartStore';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useTranslation } from 'react-i18next';
import confetti from 'react-confetti';

export interface WaiterOrderData {
  storeId: string;
  tableId: string;
  tableNumber: string;
  tableName: string;
  items: CartItem[];
  specialInstructions?: string;
  totalAmount: number;
  orderId?: string;
}

export interface WaiterOrderResponse {
  success: boolean;
  orderId?: string;
  error?: string;
}

// Function to trigger confetti animation
const triggerConfetti = () => {
  // Create a temporary div to render confetti
  const confettiContainer = document.createElement('div');
  confettiContainer.style.position = 'fixed';
  confettiContainer.style.top = '0';
  confettiContainer.style.left = '0';
  confettiContainer.style.width = '100%';
  confettiContainer.style.height = '100%';
  confettiContainer.style.pointerEvents = 'none';
  confettiContainer.style.zIndex = '9999';
  document.body.appendChild(confettiContainer);

  // Create confetti element
  const confettiElement = document.createElement('div');
  confettiContainer.appendChild(confettiElement);

  // Simulate confetti effect with CSS animations
  const colors = ['#ff6b6b', '#4ecdc4', '#45b7d1', '#96ceb4', '#feca57', '#ff9ff3'];
  
  for (let i = 0; i < 150; i++) {
    const particle = document.createElement('div');
    particle.style.position = 'absolute';
    particle.style.width = Math.random() * 10 + 5 + 'px';
    particle.style.height = Math.random() * 10 + 5 + 'px';
    particle.style.backgroundColor = colors[Math.floor(Math.random() * colors.length)];
    particle.style.borderRadius = '50%';
    particle.style.left = Math.random() * 100 + '%';
    particle.style.top = '-10px';
    particle.style.animation = `fall ${Math.random() * 3 + 2}s linear forwards`;
    particle.style.animationDelay = Math.random() * 0.5 + 's';
    
    confettiContainer.appendChild(particle);
  }

  // Add CSS animation
  const style = document.createElement('style');
  style.textContent = `
    @keyframes fall {
      to {
        transform: translateY(100vh) rotate(360deg);
        opacity: 0;
      }
    }
  `;
  document.head.appendChild(style);

  // Clean up after animation
  setTimeout(() => {
    if (document.body.contains(confettiContainer)) {
      document.body.removeChild(confettiContainer);
    }
    if (document.head.contains(style)) {
      document.head.removeChild(style);
    }
  }, 5000);
};

// Function to send push notification
const sendOrderNotification = async (orderData: WaiterOrderData) => {
  try {
    const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/send-order-notification`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
      },
      body: JSON.stringify({
        storeId: orderData.storeId,
        tableNumber: orderData.tableNumber,
        orderId: orderData.orderId,
        totalAmount: orderData.totalAmount,
        itemCount: orderData.items.length,
        message: `New order from ${orderData.tableName} - ${orderData.items.length} items`,
      }),
    });

    if (!response.ok) {
      console.error('Failed to send order notification');
    }
  } catch (error) {
    console.error('Error sending order notification:', error);
  }
};

// Main function to place waiter order
export const placeWaiterOrder = async (orderData: WaiterOrderData): Promise<WaiterOrderResponse> => {
  try {
    // Create the order in the database
    const { data: order, error: orderError } = await supabase
      .from('orders')
      .insert({
        store_id: orderData.storeId,
        table_id: orderData.tableId, // This should be the actual table UUID
        table_number: parseInt(orderData.tableNumber),
        table_name: orderData.tableName,
        status: 'pending',
        total_amount: orderData.totalAmount,
        special_instructions: orderData.specialInstructions,
        is_payed: true, // Waiter orders are treated as paid
        payment_method: 'Cash', // Default payment method for waiter orders
      })
      .select()
      .single();

    if (orderError) {
      console.error('Error creating order:', orderError);
      return {
        success: false,
        error: orderError.message,
      };
    }

    // Create order items
    const orderItems = orderData.items.map((cartItem) => ({
      order_id: order.id,
      menu_item_id: cartItem.item.id,
      menu_item_name: cartItem.item.name,
      quantity: cartItem.quantity,
      price: Number(cartItem.item.price),
      notes: cartItem.specialInstructions,
    }));

    const { error: itemsError } = await supabase
      .from('order_items')
      .insert(orderItems);

    if (itemsError) {
      console.error('Error creating order items:', itemsError);
      return {
        success: false,
        error: itemsError.message,
      };
    }

    // Trigger confetti animation
    triggerConfetti();

    // Send push notification
    await sendOrderNotification({
      ...orderData,
      orderId: order.id,
    });

    return {
      success: true,
      orderId: order.id,
    };
  } catch (error) {
    console.error('Error placing waiter order:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error occurred',
    };
  }
};

// Hook for using waiter order service
export const useWaiterOrderService = () => {
  const { toast } = useToast();
  const { t } = useTranslation();

  const placeOrder = async (orderData: WaiterOrderData) => {
    const result = await placeWaiterOrder(orderData);

    if (result.success) {
      toast({
        title: t('waiterOrdering.orderPlacedSuccessfully'),
        description: t('waiterOrdering.orderPlacedDescription', { orderId: result.orderId }),
      });
    } else {
      toast({
        title: t('waiterOrdering.failedToPlaceOrder'),
        description: result.error || t('waiterOrdering.failedToPlaceOrderDescription'),
        variant: 'destructive',
      });
    }

    return result;
  };

  return { placeOrder };
}; 